import { useState } from "react";
import { NavLink, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  QrCode,
  FileCheck2,
  Layers,
  BarChart3,
  CreditCard,
  Settings,
  LogOut,
  Search,
  Building2,
  MessageSquare,
  Send,
  Bot,
  Activity,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  ShieldCheck,
  FileText,
} from "lucide-react";

import { type CurrentUser } from "../lib/api";
import { OverviewView } from "./views/OverviewView";
import { TenantsView } from "./views/TenantsView";
import { ChannelsView } from "./views/ChannelsView";
import { QRInstancesView } from "./views/QRInstancesView";
import { QRMessageQueueView } from "./views/QRMessageQueueView";
import { DeliveryReportsDetailedView } from "./views/DeliveryReportsDetailedView";
import { UsageConnectionReportsView } from "./views/UsageConnectionReportsView";
import { GlobalDeliveryMonitorView } from "./views/GlobalDeliveryMonitorView";
import { UnifiedInboxView } from "./views/UnifiedInboxView";
import { ContactsView } from "./views/ContactsView";
import { CampaignsView } from "./views/CampaignsView";
import { TemplatesView } from "./views/TemplatesView";
import { AutomationsView } from "./views/AutomationsView";
import { TeamManagementView } from "./views/TeamManagementView";
import { ManualBillingView } from "./views/ManualBillingView";
import { SystemHealthView } from "./views/SystemHealthView";

type AdminConsoleProps = {
  token: string;
  user: CurrentUser;
  onLogout: () => void;
};

export function AdminConsole({ token, user, onLogout }: AdminConsoleProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(() => {
    return localStorage.getItem("wamaster_sidebar_expanded") === "true";
  });
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";

  function toggleSidebar() {
    setSidebarExpanded((prev) => {
      const next = !prev;
      localStorage.setItem("wamaster_sidebar_expanded", String(next));
      return next;
    });
  }

  const navGroups = [
    {
      title: "Core Platform",
      items: [
        { path: "/admin/overview", label: "Dashboard", icon: LayoutDashboard },
        { path: "/admin/team", label: "Users Directory", icon: Users },
        { path: "/admin/qr-instances", label: "Instances & QR", icon: QrCode },
        { path: "/admin/channels", label: "Meta Official WABA", icon: ShieldCheck },
      ],
    },
    {
      title: "Live Operations",
      items: [
        { path: "/admin/inbox", label: "Live Inbox", icon: MessageSquare },
        { path: "/admin/delivery-reports", label: "Delivery Reports", icon: FileCheck2 },
        { path: "/admin/qr-queue", label: "Message Queue", icon: Layers },
        { path: "/admin/usage-connections", label: "Usage & Telemetry", icon: BarChart3 },
      ],
    },
    {
      title: "CRM & Growth",
      items: [
        { path: "/admin/contacts", label: "Contacts CRM", icon: Users },
        { path: "/admin/templates", label: "Message Templates", icon: FileText },
        { path: "/admin/campaigns", label: "Broadcast Campaigns", icon: Send },
        { path: "/admin/automations", label: "Bots & Automations", icon: Bot },
        { path: "/admin/billing", label: "Recharge & Billing", icon: CreditCard },
      ],
    },
    ...(isSuperAdmin
      ? [
          {
            title: "Super Admin Control Plane",
            items: [
              { path: "/admin/tenants", label: "Organizations & Quotas", icon: Building2 },
              { path: "/admin/global-delivery", label: "Global Fleet Health", icon: Activity },
              { path: "/admin/system", label: "System Health", icon: Settings },
            ],
          },
        ]
      : [
          {
            title: "System",
            items: [{ path: "/admin/system", label: "System Diagnostics", icon: Settings }],
          },
        ]),
  ];

  const quickSearchItems = [
    { name: "Dashboard Overview", path: "/admin/overview", cat: "eNotify" },
    { name: "Users Directory", path: "/admin/team", cat: "eNotify" },
    { name: "WhatsApp Instances & Pairing", path: "/admin/qr-instances", cat: "eNotify" },
    { name: "Meta Verified Official Channels", path: "/admin/channels", cat: "Meta WABA" },
    { name: "Delivery Reports (Granular Timestamps)", path: "/admin/delivery-reports", cat: "eNotify" },
    { name: "Message Queue & Pacing", path: "/admin/qr-queue", cat: "eNotify" },
    { name: "Today's Usage & Sockets Telemetry", path: "/admin/usage-connections", cat: "eNotify" },
    { name: "Recharge History & Wallet", path: "/admin/billing", cat: "eNotify" },
    { name: "Live Omnichannel Inbox", path: "/admin/inbox", cat: "Chat" },
    { name: "Contacts CRM & Bulk Import", path: "/admin/contacts", cat: "CRM" },
    { name: "Campaign Broadcast Studio", path: "/admin/campaigns", cat: "Broadcasting" },
    { name: "Keyword Auto-Responders & Bots", path: "/admin/automations", cat: "Automations" },
    ...(isSuperAdmin ? [{ name: "Organizations & Quotas", path: "/admin/tenants", cat: "Admin" }] : []),
    ...(isSuperAdmin ? [{ name: "Global Fleet Monitor", path: "/admin/global-delivery", cat: "Admin" }] : []),
  ];

  const filteredSearch = quickSearchItems.filter(
    (i) =>
      i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.cat.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex antialiased">
      {/* 1. Left Expandable / Collapsible Side Drawer (Smooth Animation) */}
      <aside
        className={`bg-white border-r border-slate-200/90 flex flex-col justify-between transition-all duration-200 z-30 sticky top-0 h-screen shrink-0 ${
          sidebarExpanded ? "w-64" : "w-16"
        }`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          
          {/* Sidebar Top Header (Brand + Toggle Button) */}
          <div className="h-14 border-b border-slate-100 flex items-center justify-between px-3 shrink-0">
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black text-sm shadow-sm shrink-0">
                WA
              </div>
              {sidebarExpanded && (
                <div className="truncate">
                  <span className="font-extrabold text-slate-900 text-sm tracking-tight block">WA Master</span>
                  <span className="text-[10px] text-slate-400 font-mono block">Enterprise v1.1</span>
                </div>
              )}
            </div>

            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
              title={sidebarExpanded ? "Collapse Sidebar" : "Expand Sidebar"}
            >
              {sidebarExpanded ? (
                <PanelLeftClose className="w-4 h-4" />
              ) : (
                <PanelLeftOpen className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Navigation Groups List */}
          <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
            {navGroups.map((group, gIdx) => (
              <div key={gIdx} className="space-y-1">
                {sidebarExpanded && (
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2.5 py-1">
                    {group.title}
                  </div>
                )}
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      title={item.label}
                      className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs font-medium transition-all ${
                        isActive
                          ? "bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 shadow-2xs"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                      }`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-emerald-600" : "text-slate-400"}`} />
                      {sidebarExpanded && (
                        <span className="truncate whitespace-nowrap">{item.label}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Bottom Logout & User Summary */}
          <div className="p-2 border-t border-slate-100 shrink-0">
            {sidebarExpanded && (
              <div className="p-2 mb-1.5 rounded-lg bg-slate-50 border border-slate-200/60 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center shrink-0">
                  {user.username.slice(0, 2).toUpperCase()}
                </div>
                <div className="truncate text-xs">
                  <div className="font-bold text-slate-900 truncate">{user.username}</div>
                  <div className="text-[10px] text-slate-400 font-mono capitalize">{user.role}</div>
                </div>
              </div>
            )}
            <button
              onClick={onLogout}
              title="Logout from session"
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-semibold text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-all"
            >
              <LogOut className="w-4 h-4 shrink-0 text-slate-400 hover:text-rose-600" />
              {sidebarExpanded && <span>Log Out</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* 2. Main Content Wrapper */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar (Matches eNotify Header Exactly) */}
        <header className="h-14 bg-white border-b border-slate-200/90 flex items-center justify-between px-4 sticky top-0 z-20 shadow-2xs">
          {/* Left: Sidebar Toggle + Organization Identifier */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
              title="Toggle sidebar drawer"
            >
              {sidebarExpanded ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
            </button>

            <div className="h-4 w-px bg-slate-200 hidden sm:block" />

            {/* Organization Tag (eNotify exact style) */}
            <div className="flex items-center gap-2">
              <span className="text-slate-800 font-bold text-xs">
                Interlink Consultant (919323818985)
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold tracking-wide uppercase">
                {isSuperAdmin ? "SUPER ADMIN" : "TENANT"}
              </span>
            </div>
          </div>

          {/* Right: Quick Search + Theme Switcher + Profile */}
          <div className="flex items-center gap-2.5">
            {/* Quick Search Shortcut ⌘k */}
            <button
              onClick={() => setSearchModalOpen(true)}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-400 hover:text-slate-700 hover:border-slate-300 transition-all shadow-2xs"
            >
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <span>Search modules...</span>
              <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-mono text-slate-500 font-bold">
                ⌘K
              </kbd>
            </button>

            {/* Theme Toggle (Light Default) */}
            <button className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors">
              <Sun className="w-4 h-4 text-amber-500" />
            </button>

            {/* User Profile Avatar */}
            <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center border border-emerald-300 shadow-2xs">
              {user.username.slice(0, 2).toUpperCase()}
            </div>
          </div>
        </header>

        {/* 3. Main Views Area */}
        <main className="flex-1 p-5 md:p-6 overflow-x-hidden">
          <Routes>
            <Route path="/admin/overview" element={<OverviewView token={token} user={user} />} />
            <Route path="/admin/team" element={<TeamManagementView token={token} user={user} />} />
            <Route path="/admin/qr-instances" element={<QRInstancesView token={token} user={user} />} />
            <Route path="/admin/channels" element={<ChannelsView token={token} user={user} />} />
            <Route path="/admin/delivery-reports" element={<DeliveryReportsDetailedView token={token} user={user} />} />
            <Route path="/admin/qr-queue" element={<QRMessageQueueView token={token} user={user} />} />
            <Route path="/admin/usage-connections" element={<UsageConnectionReportsView token={token} user={user} />} />
            <Route path="/admin/billing" element={<ManualBillingView token={token} user={user} />} />
            <Route path="/admin/inbox" element={<UnifiedInboxView token={token} user={user} />} />
            <Route path="/admin/contacts" element={<ContactsView token={token} user={user} />} />
            <Route path="/admin/templates" element={<TemplatesView token={token} user={user} />} />
            <Route path="/admin/campaigns" element={<CampaignsView token={token} user={user} />} />
            <Route path="/admin/automations" element={<AutomationsView token={token} user={user} />} />
            {isSuperAdmin && (
              <>
                <Route path="/admin/tenants" element={<TenantsView token={token} user={user} />} />
                <Route path="/admin/global-delivery" element={<GlobalDeliveryMonitorView token={token} user={user} />} />
              </>
            )}
            <Route path="/admin/system" element={<SystemHealthView token={token} user={user} />} />
            <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
            <Route path="/" element={<Navigate to="/admin/overview" replace />} />
            <Route path="*" element={<Navigate to="/admin/overview" replace />} />
          </Routes>
        </main>

        {/* eNotify Support Footer */}
        <footer className="h-10 bg-white border-t border-slate-200/90 flex items-center justify-between px-5 text-[11px] text-slate-400">
          <div>WA Master Platform · Enterprise High Throughput Engine</div>
          <div className="flex items-center gap-3">
            <span>support@wamaster.io</span>
            <span>+91 919028083335</span>
          </div>
        </footer>
      </div>

      {/* 4. Global Quick Search Modal (⌘K) */}
      {searchModalOpen && (
        <div className="modal-backdrop" onClick={() => setSearchModalOpen(false)}>
          <div className="modal-card max-w-lg p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search modules, reports, instances, CRM..."
                className="w-full bg-transparent text-xs text-slate-900 focus:outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button onClick={() => setSearchModalOpen(false)} className="text-slate-400 text-xs font-bold px-1.5">
                ESC
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto p-2 divide-y divide-slate-100">
              {filteredSearch.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    navigate(item.path);
                    setSearchModalOpen(false);
                  }}
                  className="w-full text-left p-2 rounded-lg hover:bg-slate-50 flex items-center justify-between text-xs transition-colors"
                >
                  <span className="font-semibold text-slate-800">{item.name}</span>
                  <span className="text-[10px] font-mono text-slate-400 px-1.5 py-0.5 rounded bg-slate-100">
                    {item.cat}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
